// File: frontend/src/pages/InvestorPitch/components/slides/LeadershipSlide/LeaderProfile.js

import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import * as LucideIcons from 'lucide-react';
import styles from './LeaderProfile.module.css';

const LeaderProfile = ({ name, role, highlights, achievements, socialLinks, photoUrl }) => {
  const { t } = useLanguage();

  return (
    <motion.div 
      className={styles.container}
      whileHover={{ scale: 1.01 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      <div className={styles.profile}>
        <div className={styles.avatar}>
          {photoUrl ? (
            <img 
              src={photoUrl} 
              alt={name}
              className={styles.avatarPhoto}
              aria-label={t('investor_pitch.leadership.profile.avatar.aria_label', 'Profile photo')}
            />
          ) : (
            <LucideIcons.User 
              className={styles.avatarIcon} 
              aria-label={t('investor_pitch.leadership.profile.avatar.aria_label', 'Profile icon')}
            />
          )}
        </div>
        <div className={styles.info}>
          <div className={styles.nameSection}>
            <div className={styles.nameRole}>
              <h3 className={styles.name}>{name}</h3>
              <p className={styles.role}>{role}</p>
            </div>
            <div className={styles.socialLinks}>
              <a 
                href={socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
                aria-label={t('investor_pitch.leadership.social.linkedin.aria_label', 'LinkedIn profile')}
              >
                <img 
                  src="/icons/18/socials/linkedin/linkedin-logo.svg" 
                  alt="LinkedIn"
                  className={styles.socialIcon}
                />
              </a>
              <a 
                href={socialLinks.x}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
                aria-label={t('investor_pitch.leadership.social.x.aria_label', 'X profile')}
              >
                <img 
                  src="/icons/18/socials/x/x-logo.svg" 
                  alt="X"
                  className={styles.socialIcon}
                />
              </a>
              {socialLinks.github && (
                <a 
                  href={socialLinks.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialLink}
                  aria-label={t('investor_pitch.leadership.social.github.aria_label', 'GitHub profile')}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="18" 
                    height="18" 
                    viewBox="0 0 24 24"
                    className={styles.socialIcon}
                    fill="white"
                  >
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.highlights}>
        {highlights.map((highlight, index) => {
          const Icon = LucideIcons[highlight.icon];
          return (
            <div 
              key={index} 
              className={styles.highlight}
            >
              <div 
                className={styles.highlightIcon}
                style={{ color: highlight.color }}
              >
                <Icon size={24} />
              </div>
              <span className={styles.highlightText}>{highlight.title}</span>
            </div>
          );
        })}
      </div>

      <div className={styles.achievements}>
        {achievements.map((achievement, index) => (
          <div key={index} className={styles.achievement}>
            <div className={styles.bullet} />
            <span className={styles.achievementText}>{achievement}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

LeaderProfile.propTypes = {
  name: PropTypes.string.isRequired,
  role: PropTypes.string.isRequired,
  highlights: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
  })).isRequired,
  achievements: PropTypes.arrayOf(PropTypes.string).isRequired,
  socialLinks: PropTypes.shape({
    linkedin: PropTypes.string.isRequired,
    x: PropTypes.string.isRequired,
    github: PropTypes.string,
  }).isRequired,
  photoUrl: PropTypes.string,
};

export default LeaderProfile;